import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ShieldCheck, Database, Bot, LineChart, Server } from "lucide-react";
import LegalTabs from "@/components/LegalTabs";

const SUBPROCESSORS = [
    {
        name: "OpenAI",
        icon: Bot,
        purpose: "AI resume review, hiring brief generation, interview practice, and coaching completions.",
        data: "Resume text · job descriptions · session prompts · user-facing generated content.",
        region: "United States (SOC 2 Type II · GDPR/CCPA compliant)",
        retention: "Zero-retention API mode (0 days) for all Simonara traffic; no training on your data.",
        privacy: "https://openai.com/enterprise-privacy/",
        testId: "subprocessor-openai",
    },
    {
        name: "Google Gemini",
        icon: Bot,
        purpose: "Fallback + specialist AI matching, resume parsing enrichment.",
        data: "Resume text · job descriptions.",
        region: "United States (SOC 2 Type II · GDPR/CCPA compliant)",
        retention: "No training on customer data (Gemini API terms).",
        privacy: "https://ai.google.dev/gemini-api/terms",
        testId: "subprocessor-gemini",
    },
    {
        name: "PostHog",
        icon: LineChart,
        purpose: "Product analytics (page views, feature usage, funnel timings).",
        data: "Anonymous device IDs · click paths · signed-in user_id. No form values, no resume contents.",
        region: "United States",
        retention: "Rolling 12-month event retention.",
        privacy: "https://posthog.com/privacy",
        testId: "subprocessor-posthog",
    },
    {
        name: "Emergent (hosting + object storage)",
        icon: Server,
        purpose: "Application hosting, database (MongoDB), and object storage for resumes, avatars, and chat attachments.",
        data: "All customer data at rest and in transit.",
        region: "Kubernetes cluster in a US-primary region.",
        retention: "Retained for the life of your account · deleted within 30 days of account termination.",
        privacy: "https://emergent.sh/",
        testId: "subprocessor-emergent",
    },
    {
        name: "Resend",
        icon: Database,
        purpose: "Transactional email delivery (application ack, status change, chat notifications).",
        data: "Recipient email · subject · body.",
        region: "United States",
        retention: "Message metadata retained 30 days for deliverability audit.",
        privacy: "https://resend.com/legal/privacy-policy",
        testId: "subprocessor-resend",
    },
    {
        name: "Affinda",
        icon: Database,
        purpose: "Resume parsing (skills, years, work history extraction).",
        data: "Resume file (PDF/DOC/DOCX).",
        region: "Australia (US-region processing available on request).",
        retention: "48 hours in Affinda staging; permanently deleted after parse completes.",
        privacy: "https://www.affinda.com/privacy-policy",
        testId: "subprocessor-affinda",
    },
    {
        name: "Checkr, Inc.",
        icon: ShieldCheck,
        purpose: "Background checks on talent users (criminal, MVR).",
        data: "Candidate information necessary to run the check — name, date of birth, address, and SSN as required by the specific screening package — plus the standalone written disclosure and Checkr-collected authorization signature.",
        region: "United States (FCRA + state-compliant).",
        retention: "Retained 2 years after check completion in Simonara systems (longer only where the law requires it, e.g. adverse-action records). Checkr's own retention governed by FCRA and Checkr's terms.",
        privacy: "https://checkr.com/customer-agreement/privacy-policy",
        testId: "subprocessor-checkr",
    },
    {
        name: "Stripe",
        icon: ShieldCheck,
        purpose: "Payment processing for employer subscriptions.",
        data: "Billing contact · card last-4 · invoice metadata. Full card numbers never touch Simonara servers.",
        region: "United States (PCI DSS Level 1).",
        retention: "Retained per Stripe's own retention policy.",
        privacy: "https://stripe.com/privacy",
        testId: "subprocessor-stripe",
    },
];

export default function Subprocessors() {
    return (
        <div className="bg-background" data-testid="subprocessors-page">
            <Helmet>
                <title>Subprocessors · Simonara Talent Solutions</title>
                <meta name="description" content="The vendors Simonara uses to process customer data — what they do, what data flows to them, and where they operate." />
                <link rel="canonical" href="/subprocessors" />
            </Helmet>

            <section className="pt-16 pb-2">
                <div className="mx-auto max-w-4xl px-6">
                    <p className="overline inline-flex items-center gap-1"><Database size={11} /> Legal</p>
                    <h1 className="mt-2 font-heading font-medium tracking-tight text-4xl sm:text-5xl text-foreground">
                        Subprocessors
                    </h1>
                    <p className="mt-3 text-base text-muted-foreground max-w-2xl">
                        The vendors Simonara uses to process customer data. Every entry lists what they do, what data flows to them, where they operate, and how long they hold it. Version 2026-02-15.
                    </p>
                </div>
            </section>
            <LegalTabs />

            <section className="pb-20 pt-8">
                <div className="mx-auto max-w-4xl px-6 space-y-4">
                    {SUBPROCESSORS.map((s) => (
                        <div key={s.name} className="card-soft p-6" data-testid={s.testId}>
                            <div className="flex items-start gap-4">
                                <div className="w-10 h-10 rounded-xl bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 flex items-center justify-center shrink-0">
                                    <s.icon size={18} />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <h2 className="font-heading font-semibold text-foreground text-lg">{s.name}</h2>
                                        <a href={s.privacy} target="_blank" rel="noreferrer" className="text-[11px] underline text-muted-foreground hover:text-foreground">
                                            privacy policy →
                                        </a>
                                    </div>
                                    <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                                        <div><span className="text-muted-foreground font-medium">Purpose:</span> <span className="text-foreground">{s.purpose}</span></div>
                                        <div><span className="text-muted-foreground font-medium">Data shared:</span> <span className="text-foreground">{s.data}</span></div>
                                        <div><span className="text-muted-foreground font-medium">Region:</span> <span className="text-foreground">{s.region}</span></div>
                                        <div><span className="text-muted-foreground font-medium">Retention:</span> <span className="text-foreground">{s.retention}</span></div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}

                    <div className="mt-8 rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-5 text-sm text-foreground">
                        <p className="font-medium">Change notifications</p>
                        <p className="mt-1 text-muted-foreground">
                            Simonara notifies employers <strong className="text-foreground">30 days before</strong> onboarding a new subprocessor for customer data. Customers on a signed{" "}
                            <Link to="/dpa" className="underline text-foreground font-medium" data-testid="subprocessors-link-dpa">Data Processing Agreement</Link> can object in writing during the notice window. Questions? Email{" "}
                            <a href="mailto:privacy@simonaratalent.us" className="underline text-foreground font-medium">privacy@simonaratalent.us</a>.
                        </p>
                    </div>
                </div>
            </section>
        </div>
    );
}
